import { useMemo } from "react";
import { AlertTriangle, Scale, TrendingDown, TrendingUp } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";

import { formatCurrency } from "@/lib/currency";
import type {
  CashFlowRow,
  DisplayCurrency,
  ViewPeriod,
} from "@/hooks/finance/cash-flow/useCashFlow";
import { useBankRealityVsReport } from "@/hooks/finance/cash-flow/useBankRealityVsReport";
import { makeFormatValue } from "./statement-table.utils";

type Props = {
  rows: CashFlowRow[];
  periodKeys: string[];
  periodLabels: { key: string; label: string }[];
  dateFrom: string;
  dateTo: string;
  viewPeriod: ViewPeriod;
  displayCurrency: DisplayCurrency;
  isLoading: boolean;
};

function formatNative(value: number, ccy: string) {
  try {
    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 0,
    }).format(value) + " " + ccy;
  } catch {
    return `${value.toFixed(0)} ${ccy}`;
  }
}

export function BankRealityStrip({
  rows,
  periodKeys,
  periodLabels,
  dateFrom,
  dateTo,
  viewPeriod,
  displayCurrency,
  isLoading,
}: Props) {
  const reality = useBankRealityVsReport({
    dateFrom,
    dateTo,
    viewPeriod,
    periodKeys,
    displayCurrency,
  });

  const reportedByPeriod = useMemo(() => {
    const m = new Map<string, number>();
    const netOp = rows.find((r) => r.key === "net-operating");
    if (!netOp) return m;
    for (const pk of periodKeys) m.set(pk, netOp.values.get(pk) ?? 0);
    return m;
  }, [rows, periodKeys]);

  const fmt = makeFormatValue(displayCurrency);

  const loading = isLoading || reality.isLoading;

  // Totals across the whole range
  const totals = useMemo(() => {
    let realOpening: number | null = null;
    let realClosing: number | null = null;
    let reported = 0;
    let missingOpening = 0;
    let missingClosing = 0;
    let total = 0;

    if (periodKeys.length > 0) {
      const first = reality.byPeriod.get(periodKeys[0]);
      const last = reality.byPeriod.get(periodKeys[periodKeys.length - 1]);
      realOpening = first?.realOpening ?? null;
      realClosing = last?.realClosing ?? null;
      missingOpening = first?.missingOpening ?? 0;
      missingClosing = last?.missingClosing ?? 0;
      total = first?.totalAccounts ?? 0;
    }
    for (const pk of periodKeys) reported += reportedByPeriod.get(pk) ?? 0;

    const realBurn =
      realOpening != null && realClosing != null ? realClosing - realOpening : null;
    const gap = realBurn != null ? realBurn - reported : null;

    return { realOpening, realClosing, realBurn, reported, gap, missingOpening, missingClosing, total };
  }, [reality.byPeriod, periodKeys, reportedByPeriod]);

  return (
    <Card className="border-none shadow-lg overflow-hidden bg-white">
      <CardHeader className="bg-gradient-to-r from-indigo-900 to-slate-900 border-none pb-4">
        <div className="flex justify-between items-start gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-white/10">
              <Scale className="h-4 w-4 text-indigo-200" />
            </div>
            <div>
              <CardTitle className="text-white text-base">
                Bank Reality vs Report
              </CardTitle>
              <CardDescription className="text-slate-400 text-[10px]">
                Real bank cash position vs reported Net Operating, per period · {displayCurrency}
              </CardDescription>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 text-right text-white">
            <div className="text-[9px] uppercase tracking-widest text-slate-400">
              Period gap
            </div>
            <div
              className={`text-lg font-black tabular-nums ${
                totals.gap == null
                  ? "text-slate-300"
                  : Math.abs(totals.gap) < 1
                  ? "text-emerald-300"
                  : totals.gap > 0
                  ? "text-amber-300"
                  : "text-rose-300"
              }`}
            >
              {totals.gap == null ? "—" : fmt(totals.gap)}
            </div>
            <div className="text-[10px] text-slate-400">
              Real burn {totals.realBurn == null ? "—" : fmt(totals.realBurn)} ·
              Reported {fmt(totals.reported)}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-4 space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </div>
        ) : (
          <TooltipProvider delayDuration={150}>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-3 py-2 font-semibold text-slate-700 sticky left-0 bg-slate-50 z-10 min-w-[180px]">
                      Metric
                    </th>
                    {periodLabels.map((p) => (
                      <th
                        key={p.key}
                        className="text-right px-2 py-2 font-semibold text-slate-700 whitespace-nowrap min-w-[100px]"
                      >
                        {p.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {/* Real Opening */}
                  <tr className="hover:bg-slate-50">
                    <td className="px-3 py-1.5 text-slate-600 sticky left-0 bg-white z-10">
                      Real Opening
                    </td>
                    {periodKeys.map((pk) => {
                      const row = reality.byPeriod.get(pk);
                      const v = row?.realOpening ?? null;
                      const native = row?.byCurrencyOpening ?? {};
                      const hasNative = Object.keys(native).length > 0;
                      return (
                        <td
                          key={pk}
                          className="px-2 py-1.5 text-right tabular-nums text-slate-700"
                        >
                          {hasNative ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-help underline decoration-dotted decoration-slate-300 underline-offset-2">
                                  {v == null ? "—" : fmt(v)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-[11px]">
                                <div className="font-semibold mb-1">Native breakdown</div>
                                {Object.entries(native)
                                  .sort(([a], [b]) => a.localeCompare(b))
                                  .map(([ccy, amt]) => (
                                    <div key={ccy} className="tabular-nums">
                                      {formatNative(amt, ccy)}
                                    </div>
                                  ))}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span>—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Real Closing */}
                  <tr className="hover:bg-slate-50">
                    <td className="px-3 py-1.5 text-slate-600 sticky left-0 bg-white z-10">
                      Real Closing
                    </td>
                    {periodKeys.map((pk) => {
                      const row = reality.byPeriod.get(pk);
                      const v = row?.realClosing ?? null;
                      const native = row?.byCurrencyClosing ?? {};
                      const hasNative = Object.keys(native).length > 0;
                      return (
                        <td
                          key={pk}
                          className="px-2 py-1.5 text-right tabular-nums text-slate-700"
                        >
                          {hasNative ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-help underline decoration-dotted decoration-slate-300 underline-offset-2">
                                  {v == null ? "—" : fmt(v)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-[11px]">
                                <div className="font-semibold mb-1">Native breakdown</div>
                                {Object.entries(native)
                                  .sort(([a], [b]) => a.localeCompare(b))
                                  .map(([ccy, amt]) => (
                                    <div key={ccy} className="tabular-nums">
                                      {formatNative(amt, ccy)}
                                    </div>
                                  ))}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span>—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Real Cash Burn */}
                  <tr className="bg-slate-50/50">
                    <td className="px-3 py-1.5 font-semibold text-slate-800 sticky left-0 bg-slate-50/50 z-10">
                      Real Cash Burn
                    </td>
                    {periodKeys.map((pk) => {
                      const row = reality.byPeriod.get(pk);
                      const v = row?.realBurn ?? null;
                      return (
                        <td
                          key={pk}
                          className={`px-2 py-1.5 text-right tabular-nums font-semibold ${
                            v == null
                              ? "text-slate-400"
                              : v >= 0
                              ? "text-emerald-700"
                              : "text-rose-700"
                          }`}
                        >
                          <span className="inline-flex items-center gap-1 justify-end">
                            {v != null && v !== 0 &&
                              (v > 0 ? (
                                <TrendingUp className="h-3 w-3" />
                              ) : (
                                <TrendingDown className="h-3 w-3" />
                              ))}
                            {v == null ? "—" : fmt(v)}
                          </span>
                        </td>
                      );
                    })}
                  </tr>

                  {/* Reported Net Operating */}
                  <tr className="hover:bg-slate-50">
                    <td className="px-3 py-1.5 text-slate-600 sticky left-0 bg-white z-10">
                      Reported Net Operating
                    </td>
                    {periodKeys.map((pk) => {
                      const v = reportedByPeriod.get(pk) ?? 0;
                      return (
                        <td
                          key={pk}
                          className={`px-2 py-1.5 text-right tabular-nums ${
                            v >= 0 ? "text-emerald-700" : "text-rose-700"
                          }`}
                        >
                          {fmt(v)}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Gap */}
                  <tr className="border-t-2 border-slate-300 bg-amber-50/40">
                    <td className="px-3 py-2 font-black text-slate-900 sticky left-0 bg-amber-50/40 z-10">
                      Gap (Real − Reported)
                    </td>
                    {periodKeys.map((pk) => {
                      const row = reality.byPeriod.get(pk);
                      const real = row?.realBurn ?? null;
                      const reported = reportedByPeriod.get(pk) ?? 0;
                      const gap = real == null ? null : real - reported;
                      return (
                        <td
                          key={pk}
                          className={`px-2 py-2 text-right tabular-nums font-black ${
                            gap == null
                              ? "text-slate-400"
                              : Math.abs(gap) < 1
                              ? "text-emerald-700"
                              : gap > 0
                              ? "text-amber-700"
                              : "text-rose-700"
                          }`}
                        >
                          {gap == null ? "—" : fmt(gap)}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Coverage row */}
                  <tr className="bg-slate-50">
                    <td className="px-3 py-1.5 text-[10px] text-slate-500 sticky left-0 bg-slate-50 z-10">
                      Snapshot Coverage
                    </td>
                    {periodKeys.map((pk) => {
                      const row = reality.byPeriod.get(pk);
                      const missing = Math.max(
                        row?.missingOpening ?? 0,
                        row?.missingClosing ?? 0
                      );
                      const total = row?.totalAccounts ?? 0;
                      return (
                        <td
                          key={pk}
                          className="px-2 py-1.5 text-right text-[10px]"
                        >
                          {missing === 0 ? (
                            <Badge
                              variant="outline"
                              className="text-[9px] border-emerald-200 text-emerald-700 bg-emerald-50"
                            >
                              {total}/{total}
                            </Badge>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant="outline"
                                  className="text-[9px] border-amber-200 text-amber-700 bg-amber-50 cursor-help inline-flex items-center gap-1"
                                >
                                  <AlertTriangle className="h-2.5 w-2.5" />
                                  {missing} missing
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-[11px]">
                                {missing} of {total} accounts lack an opening or closing snapshot for this period.
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="px-3 py-2 text-[10px] text-slate-500 border-t border-slate-100 bg-slate-50">
              Gap = Real Cash Burn − Reported Net Operating. A positive gap means
              the report shows a worse outflow than the bank actually lost; a
              negative gap means the report under-states what left the bank.
            </div>
          </TooltipProvider>
        )}
      </CardContent>
    </Card>
  );
}
